import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { User, Briefcase, MapPin, Building2, Phone, Save, Camera, Clock, Trash2, MessageCircle, Calendar, Cake, Mail, AtSign, ImagePlus, FileText, Link as LinkIcon, CheckCircle2, AlertCircle } from "lucide-react";
import { TIMEZONES, guessTimezone, localTimeIn } from "@/lib/timezones";
import { resizeImageToDataURL } from "@/lib/avatar";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({
    first_name: "",
    discord_name: "",
    title: "",
    department: "",
    location: "",
    timezone: "",
    start_date: "",
    birthdate: "",
    phone: "",
    work_email: "",
    personal_email: "",
    bio: "",
    avatar_url: "",
    cover_url: "",
    job: "",
  });
  const [taxonomies, setTaxonomies] = useState({ location: [], department: [], job: [] });
  const [positions, setPositions] = useState([]);
  const [showAgreement, setShowAgreement] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    if (user) {
      setF({
        first_name: user.first_name || user.name || "",
        discord_name: user.discord_name || "",
        title: user.title || "",
        department: user.department || "",
        location: user.location || "",
        timezone: user.timezone || guessTimezone(),
        start_date: user.start_date || "",
        birthdate: user.birthdate || "",
        phone: user.phone || "",
        work_email: user.work_email || "",
        personal_email: user.personal_email || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || "",
        cover_url: user.cover_url || "",
        job: user.job || "",
      });
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const [loc, dept, job] = await Promise.all([
          api.get("/taxonomies", { params: { kind: "location" } }),
          api.get("/taxonomies", { params: { kind: "department" } }),
          api.get("/taxonomies", { params: { kind: "job" } }),
        ]);
        setTaxonomies({ location: loc.data, department: dept.data, job: job.data });
      } catch (e) {
        console.debug("taxonomies load failed:", e?.message);
      }
    })();
    api.get("/positions").then(({ data }) => setPositions(data)).catch((e) => console.debug("positions load failed:", e?.message));
  }, []);

  if (!user) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() && !f.discord_name.trim()) {
      toast.error("At least a first name or discord name is required");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, f);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataURL(file, { maxDim: 400, quality: 0.82 });
      setF((s) => ({ ...s, avatar_url: dataUrl }));
      await api.patch(`/users/${user.id}`, { avatar_url: dataUrl });
      await refresh();
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    }
  };

  const removeAvatar = async () => {
    setF((s) => ({ ...s, avatar_url: "" }));
    await api.patch(`/users/${user.id}`, { avatar_url: "" });
    await refresh();
    toast.success("Avatar removed");
  };

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      // Cover is wider than avatar — allow up to 1400px and slightly lower quality to keep base64 small
      const dataUrl = await resizeImageToDataURL(file, { maxDim: 1400, quality: 0.78 });
      setF((s) => ({ ...s, cover_url: dataUrl }));
      await api.patch(`/users/${user.id}`, { cover_url: dataUrl });
      await refresh();
      toast.success("Cover photo updated");
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    }
  };

  const removeCover = async () => {
    setF((s) => ({ ...s, cover_url: "" }));
    await api.patch(`/users/${user.id}`, { cover_url: "" });
    await refresh();
    toast.success("Cover photo removed");
  };

  const acknowledgeAgreement = async () => {
    setAcknowledging(true);
    try {
      await api.post("/users/me/acknowledge-position-agreement");
      await refresh();
      toast.success("Agreement acknowledged — onboarding complete!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to acknowledge");
    } finally {
      setAcknowledging(false);
    }
  };

  const initials = (f.first_name || user.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const clock = localTimeIn(f.timezone);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Your Profile</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Edit profile</h1>
        <p className="text-sm text-slate-400 mt-1">
          Everyone in the directory can see your name, discord tag, role, title, department, location, timezone, start date and birthday.
        </p>
      </div>

      {/* ONBOARDING GATE — must acknowledge staff agreement */}
      {user?.position_id && !user?.position_acknowledged_at && (() => {
        const pos = positions.find((p) => p.id === user.position_id);
        if (!pos || !(pos.agreement_body || pos.agreement_url)) return null;
        return (
          <div className="rounded-xl border-2 border-[color:var(--accent)] bg-[color:var(--accent)]/8 p-4 flex items-start gap-3" data-testid="onboarding-gate-banner">
            <AlertCircle size={20} className="text-[color:var(--accent)] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[color:var(--accent)]">Onboarding step required</div>
              <div className="text-xs text-slate-300 mt-0.5">
                You've been assigned <strong>{pos.name}</strong>. Review and acknowledge the staff agreement below to finish onboarding.
              </div>
            </div>
          </div>
        );
      })()}

      <div className="card-surface overflow-hidden" data-testid="profile-card">
        {/* COVER BANNER */}
        <div className="relative">
          {f.cover_url ? (
            <img
              src={f.cover_url}
              alt="cover"
              data-testid="profile-cover-img"
              className="w-full h-36 object-cover"
            />
          ) : (
            <div className="cover-fallback w-full h-36" data-testid="profile-cover-fallback" />
          )}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => coverRef.current?.click()}
              data-testid="profile-cover-upload"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-black/60 hover:bg-black/80 backdrop-blur px-3 py-1.5 rounded-full transition-colors"
              title={f.cover_url ? "Change cover" : "Add cover"}
            >
              <ImagePlus size={13} />
              {f.cover_url ? "Change cover" : "Add cover"}
            </button>
            {f.cover_url && (
              <button
                type="button"
                onClick={removeCover}
                data-testid="profile-cover-remove"
                title="Remove cover"
                className="inline-flex items-center justify-center w-7 h-7 text-white bg-black/60 hover:bg-[#ff3b5c]/90 backdrop-blur rounded-full transition-colors"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
          <input
            ref={coverRef}
            type="file"
            accept="image/*"
            onChange={(e) => uploadCover(e.target.files?.[0])}
            className="hidden"
            data-testid="profile-cover-input"
          />
        </div>

        <div className="px-6 pb-6">
          <div className="flex items-end gap-5 -mt-10 mb-6 pb-6 border-b border-slate-800 relative">
            <div className="relative">
              {f.avatar_url ? (
                <img src={f.avatar_url} alt={f.first_name} className="w-20 h-20 rounded-full object-cover border-4 border-[#121620]" data-testid="profile-avatar-img" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#242c3d] grid place-items-center font-black text-[color:var(--accent)] text-2xl border-4 border-[#121620]">
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="profile-avatar-upload"
                title="Upload photo"
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full grid place-items-center text-black shadow-lg"
                style={{ background: "var(--accent)" }}
              >
                <Camera size={14} />
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadAvatar(e.target.files?.[0])} className="hidden" data-testid="profile-avatar-input" />
            </div>
            <div className="flex-1 pb-1">
              <div className="text-xl font-bold">
                {f.first_name || user.email}
                {f.discord_name && <span className="text-[color:var(--accent)] font-normal text-base ml-2">({f.discord_name})</span>}
              </div>
              <div className="text-xs text-slate-400 uppercase tracking-widest mt-0.5">{user.role} · {user.email}</div>
              {f.avatar_url && (
                <button type="button" onClick={removeAvatar} data-testid="profile-avatar-remove" className="mt-2 text-xs text-slate-400 hover:text-[#ff3b5c] inline-flex items-center gap-1">
                  <Trash2 size={12} /> Remove photo
                </button>
              )}
            </div>
          </div>

        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="First name" icon={<User size={14} />} required>
            <input data-testid="profile-first-name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required className="input" />
          </Field>
          <Field label="Discord name" icon={<MessageCircle size={14} />}>
            <input data-testid="profile-discord-name" value={f.discord_name} onChange={(e) => setF({ ...f, discord_name: e.target.value })} placeholder="e.g. jonvard_" className="input" />
          </Field>
          <Field label="Job title" icon={<Briefcase size={14} />}>
            <input data-testid="profile-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Senior Community Manager" className="input" />
          </Field>
          <Field label="Department" icon={<Building2 size={14} />}>
            <TaxonomyInput value={f.department} onChange={(v) => setF({ ...f, department: v })} options={taxonomies.department} placeholder="Pick or type a department" testid="profile-department" />
          </Field>
          <Field label="Job" icon={<Briefcase size={14} />}>
            <TaxonomyInput value={f.job} onChange={(v) => setF({ ...f, job: v })} options={taxonomies.job} placeholder="Pick or type a job (e.g. Stream Manager)" testid="profile-job" />
          </Field>
          <Field label="Location" icon={<MapPin size={14} />}>
            <TaxonomyInput value={f.location} onChange={(v) => setF({ ...f, location: v })} options={taxonomies.location} placeholder="Pick or type a location" testid="profile-location" />
          </Field>
          <Field label={<>Timezone {clock && <span className="ml-2 text-[10px] font-normal text-[color:var(--accent)] normal-case tracking-normal">now {clock}</span>}</>} icon={<Clock size={14} />}>
            <select data-testid="profile-timezone" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} className="input">
              <option value="">Not set</option>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Field>
          <Field label="Start date" icon={<Calendar size={14} />}>
            <input type="date" data-testid="profile-start-date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="input" />
          </Field>
          <Field label="Birthday" icon={<Cake size={14} />}>
            <input type="date" data-testid="profile-birthdate" value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} className="input" />
          </Field>
          <Field label="Phone" icon={<Phone size={14} />}>
            <input data-testid="profile-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Optional" className="input" />
          </Field>
          <div className="hidden md:block" />
          <div className="md:col-span-2 pt-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold flex items-center gap-2">
              <AtSign size={12} /> <span>Contact emails</span>
              <span className="flex-1 h-px bg-[#2a3447]/60" />
              <span className="normal-case tracking-normal text-slate-500">Login email: <span className="text-slate-300">{user.email}</span></span>
            </div>
          </div>
          <Field label="Work email" icon={<Mail size={14} />}>
            <input type="email" data-testid="profile-work-email" value={f.work_email} onChange={(e) => setF({ ...f, work_email: e.target.value })} placeholder="e.g. you@tragedysmadhouse.net" className="input" />
          </Field>
          <Field label="Personal email" icon={<Mail size={14} />}>
            <input type="email" data-testid="profile-personal-email" value={f.personal_email} onChange={(e) => setF({ ...f, personal_email: e.target.value })} placeholder="Optional backup contact" className="input" />
          </Field>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">About you</div>
            <textarea data-testid="profile-bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={4} placeholder="A short bio your teammates will see." className="input resize-none" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={busy} data-testid="profile-save" className="px-5 py-2.5 rounded-lg font-bold text-black flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)" }}>
              <Save size={14} /> {busy ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>
        </div>
      </div>

      {/* POSITION & STAFF AGREEMENT (read-only, assigned by admin/manager) */}
      {user?.position_id && (() => {
        const pos = positions.find((p) => p.id === user.position_id);
        if (!pos) return null;
        return (
          <div className="card-surface p-6" data-testid="profile-position-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/40 grid place-items-center text-[color:var(--accent)] shrink-0">
                <Briefcase size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-500">Your position</div>
                <div className="text-xl font-bold" data-testid="profile-position-name">{pos.name}</div>
                {pos.description && <div className="text-sm text-slate-300 mt-1">{pos.description}</div>}
                {user.position_assigned_at && (
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                    Assigned {new Date(user.position_assigned_at).toLocaleDateString()}
                  </div>
                )}
                {(pos.agreement_body || pos.agreement_url) && (
                  <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      {pos.agreement_body && (
                        <button
                          type="button"
                          onClick={() => setShowAgreement((s) => !s)}
                          data-testid="profile-toggle-agreement"
                          className="text-xs inline-flex items-center gap-1.5 text-[color:var(--accent)] hover:underline"
                        >
                          <FileText size={12} /> {showAgreement ? "Hide staff agreement" : "View staff agreement"}
                        </button>
                      )}
                      {pos.agreement_url && (
                        <a
                          href={pos.agreement_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="profile-agreement-external"
                          className="text-xs inline-flex items-center gap-1.5 text-slate-400 hover:text-[color:var(--accent)]"
                        >
                          <LinkIcon size={12} /> External copy
                        </a>
                      )}
                    </div>
                    {showAgreement && pos.agreement_body && (
                      <pre data-testid="profile-agreement-body" className="text-[12px] text-slate-300 bg-[#0b0d12] border border-slate-800 rounded-lg p-4 whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">
                        {pos.agreement_body}
                      </pre>
                    )}

                    {/* ACKNOWLEDGEMENT GATE */}
                    {user.position_acknowledged_at ? (
                      <div className="flex items-center gap-2 rounded-lg bg-[#00e5ff]/10 border border-[#00e5ff]/40 px-3 py-2" data-testid="profile-agreement-acknowledged">
                        <CheckCircle2 size={16} className="text-[#00e5ff] shrink-0" />
                        <div className="text-xs text-slate-200">
                          Agreement acknowledged on <strong>{new Date(user.position_acknowledged_at).toLocaleDateString()}</strong> at {new Date(user.position_acknowledged_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg bg-[color:var(--accent)]/8 border border-[color:var(--accent)]/40 p-3 space-y-2" data-testid="profile-acknowledge-panel">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={acknowledgeChecked}
                            onChange={(e) => setAcknowledgeChecked(e.target.checked)}
                            data-testid="profile-acknowledge-checkbox"
                            className="accent-[color:var(--accent)] w-4 h-4 mt-0.5 shrink-0 cursor-pointer"
                          />
                          <span className="text-xs text-slate-200 leading-relaxed">
                            I have read and understood the staff agreement for <strong>{pos.name}</strong> and agree to the duties &amp; responsibilities described above.
                          </span>
                        </label>
                        <button
                          type="button"
                          onClick={acknowledgeAgreement}
                          disabled={!acknowledgeChecked || acknowledging}
                          data-testid="profile-acknowledge-btn"
                          className="w-full px-4 py-2 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <CheckCircle2 size={14} /> {acknowledging ? "Recording…" : "Acknowledge & finish onboarding"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-3 text-[11px] text-slate-500">
                  Positions are assigned by an admin or manager. To change your position, reach out to your manager.
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Field({ label, icon, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
        {icon}
        {label}
        {required && <span className="text-[#ff3b5c] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}

function TaxonomyInput({ value, onChange, options, placeholder, testid }) {
  const listId = `${testid}-list`;
  const opts = Array.isArray(options) ? options : [];
  return (
    <>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} list={opts.length ? listId : undefined} placeholder={placeholder} className="input" />
      {opts.length > 0 && <datalist id={listId}>{opts.map((o) => <option key={o.id} value={o.name} />)}</datalist>}
    </>
  );
}
